// This provider ensures that the user logs in before they can use the application.
// It protects all routes.

import React from 'react';
import AuthError from './AuthError';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { hasAccess } from '../helper/authms';
import Loader from './Loader';

type Props = React.PropsWithChildren<unknown>;

const AuthManagerMS: React.FunctionComponent<Props> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const activeAccount = instance.getActiveAccount() ?? (accounts.length > 0 ? accounts[0] : null);
  const hasForcedRefresh = React.useRef(false);
  const [isRefreshingClaims, setIsRefreshingClaims] = React.useState(false);

  if (activeAccount && !instance.getActiveAccount()) {
    instance.setActiveAccount(activeAccount);
  }

  React.useEffect(() => {
    if (!activeAccount || inProgress !== InteractionStatus.None || hasForcedRefresh.current) {
      return;
    }

    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const isBrowserReload = navigationEntry?.type === 'reload';

    if (!isBrowserReload) {
      return;
    }

    if (isBrowserReload) {
      console.log('Browser reload detected, forcing Azure token refresh to update role claims.');
    }

    hasForcedRefresh.current = true;
    setIsRefreshingClaims(true);

    instance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: activeAccount,
      forceRefresh: true,
    }).then((response) => {
      if (response?.account) {
        instance.setActiveAccount(response.account);
      }
    }).catch((e) => {
      console.error('Token refresh failed', e);
    }).finally(() => {
      setIsRefreshingClaims(false);
    });
  }, [activeAccount, inProgress, instance]);

  if (!activeAccount && inProgress === InteractionStatus.None) {
    console.error("No logged in user");
    instance.loginRedirect({
      scopes: ['User.Read'],
    }).catch((e) => {
      console.error('Login redirect failed', e);
    });
  }

  if (!activeAccount && inProgress !== InteractionStatus.None) {
    return <Loader />;
  }

  if (isRefreshingClaims) {
    return <Loader />;
  }

  if (!hasAccess(activeAccount) && activeAccount) {
    return <AuthError error={undefined} message={"You do not have access to this application."} />;
  }

  return <>{children}</>;

};

export default AuthManagerMS;
